import React, { useState, useEffect } from "react";
import { Section, QuestionTypeMaster } from "../../types";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { Input, Select } from "../ui/FormFields";
import { toast } from "react-hot-toast";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  editingBank?: any;
  sections: Section[];
  traits: any[];
  questionTypes: QuestionTypeMaster[];
  preselectedSectionId?: string;
}

const QuestionBankModal: React.FC<Props> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editingBank, 
  sections, 
  preselectedSectionId 
}) => {
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [bankName, setBankName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editingBank) {
        setBankName(editingBank.name);
        setSelectedSectionId(editingBank.sectionId);
        const section = sections.find(s => s.id === editingBank.sectionId);
        setSelectedSection(section || null);
      } else if (preselectedSectionId) {
        setSelectedSectionId(preselectedSectionId);
        const section = sections.find(s => s.id === preselectedSectionId);
        setSelectedSection(section || null);
        setBankName("");
      } else {
        setBankName("");
        setSelectedSectionId("");
        setSelectedSection(null);
      }
    }
  }, [isOpen, editingBank, preselectedSectionId, sections]);

  const handleSectionChange = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    const section = sections.find(s => s.id === sectionId);
    setSelectedSection(section || null);
  };

  const handleSubmit = async () => {
    if (!bankName.trim()) {
      toast.error("Bank name is required");
      return;
    }
    if (!selectedSectionId) {
      toast.error("Please select a section");
      return;
    }

    setLoading(true);
    try {
      await onSave({
        name: bankName.trim(),
        sectionId: selectedSectionId,
        description: `Question bank for ${selectedSection?.name}`,
      });
      onClose();
    } catch (error) {
      console.error("Error saving bank:", error);
    } finally {
      setLoading(false);
    }
  };

  const sectionOptions = sections.map(s => ({
    value: s.id,
    label: `${s.name} (${s.sectionType === 'mixed' ? 'Hybrid' : s.sectionType})`
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingBank ? "Edit Question Bank" : "Create Question Bank"}
      size="md"
    >
      <div className="space-y-5">
        <div>
          <Select
            label="Select Section"
            value={selectedSectionId}
            onChange={handleSectionChange}
            options={[
              { value: "", label: "-- Select a section --" },
              ...sectionOptions
            ]}
            required
            disabled={!!editingBank || !!preselectedSectionId}
          />
        </div>

        {selectedSection && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <span>📋</span> Section Details
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Section Type</p>
                <p className="font-medium text-gray-800 capitalize">
                  {selectedSection.sectionType === 'mixed' ? 'Hybrid' : selectedSection.sectionType}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Total Questions</p>
                <p className="font-medium text-gray-800">{selectedSection.questionCount}</p>
              </div>
              <div>
                <p className="text-gray-500">Duration</p>
                <p className="font-medium text-gray-800">{selectedSection.duration} minutes</p>
              </div>
              <div>
                <p className="text-gray-500">Weightage</p>
                <p className="font-medium text-gray-800">{selectedSection.weightage}%</p>
              </div>
              {selectedSection.passingScore > 0 && (
                <div>
                  <p className="text-gray-500">Passing Score</p>
                  <p className="font-medium text-gray-800">{selectedSection.passingScore}%</p>
                </div>
              )}
              {selectedSection.isOptional && (
                <div>
                  <p className="text-gray-500">Optional</p>
                  <p className="font-medium text-green-600">Yes</p>
                </div>
              )}
            </div>
            {selectedSection.instructions && (
              <div className="mt-3 pt-3 border-t border-indigo-100">
                <p className="text-gray-500 text-xs mb-1">Instructions:</p>
                <p className="text-sm text-gray-700">{selectedSection.instructions}</p>
              </div>
            )}
          </div>
        )}

        <div>
          <Input
            label="Bank Name"
            value={bankName}
            onChange={setBankName}
            placeholder={`e.g., ${selectedSection?.name || "Section"} Question Bank`}
            required
          />
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-gradient-to-r from-indigo-600 to-purple-600"
            disabled={loading || !bankName.trim() || !selectedSectionId}
          >
            {loading ? "Saving..." : (editingBank ? "Update" : "Create")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QuestionBankModal;